import { LanguageLinksContext } from '@mnd-apps/public/contexts/LanguageLinksContext';
import { track } from '@mnd-apps/public/lib/tracker';
import Accordion, { type AccordionItems } from '@mnd-frontend/ui/public/components/Accordion';
import Button from '@mnd-frontend/ui/public/components/Button';
import { LabelBase } from '@mnd-frontend/ui/public/components/Collapsible';
import NavLanguage from '@mnd-frontend/ui/public/components/Navigation/NavLanguage';
import type { CurrentLocaleDataType } from '@mnd-frontend/ui/public/types/locale';
import type { ExtraNavItemType, PrimaryNavItemType } from '@mnd-frontend/ui/public/types/menu';
import { formatCardMenuItem, trackMenuClick } from '@mnd-frontend/ui/public/utils/formatters/menu';
import { CUBIC_BEZIER } from '@mnd-frontend/ui/shared/constants/animations';
import {
  BREAKPOINTS,
  SIZING_2,
  SPACING_1,
  SPACING_2,
  WEB_GREY_REG,
  WEB_PURPLE_REG,
  WEB_WHITE_REG,
} from '@mnd-frontend/ui/shared/tokens';
import { Squash as Hamburger } from 'hamburger-react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { Fragment, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

const NavItemCard = dynamic(
  () => import('@mnd-frontend/ui/public/components/Navigation/NavItemCard'),
);

// #region Styled Component
const NavWrapper = styled.div`
  display: flex;
  gap: ${SPACING_1};

  @media (min-width: ${BREAKPOINTS.XL}px) {
    display: none;
  }
`;

const ContentWrapperNav = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: fixed;
  top: var(--header-height, 3.75rem);
  left: 0;
  right: 0;
  padding: 0 ${SPACING_2};
  background: ${WEB_WHITE_REG};
  clip-path: inset(0px 0px 100%);
  transition: clip-path 300ms ${CUBIC_BEZIER};
  
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      @supports (height: 100dvh) {
        height: calc(100dvh - var(--header-height));
      }
      height: calc(100vh - var(--header-height));
      clip-path: inset(0px 0px 0%);
      overflow-y: auto;
    `}

  @media (min-width: ${BREAKPOINTS.XL}px) {
    display: none;
  }
`;

const NavLink = styled(NextLink)`
  ${LabelBase};

  font-size: ${SIZING_2};
  font-weight: 600;
`;

const NavLabel = styled.div`
  font-size: ${SIZING_2};
  font-weight: 600;
`;

const ListLinkItems = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_2};
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StickyBottom = styled.div<{ $isSticky: boolean }>`
  background-color: ${WEB_WHITE_REG};
  padding-bottom: ${SPACING_2};
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};
  padding-top: ${SPACING_1};
  border-top: 1px solid ${WEB_GREY_REG};

  ${({ $isSticky }) =>
    $isSticky &&
    css`
      position: sticky;
      bottom: 0;
  `}

  a > div {
    display: block;
    width: 100%;
  }

  .link-plain {
    width: 100%;
    padding: ${SPACING_1} 0;
    display: block;
    text-align: center;
    font-size: ${SIZING_2};
    font-weight: 600;
  }
`;
// #endregion

const IS_MOBILE = true;
// #region Start Component
const HeaderNavMobile = ({
  primaryNav,
  extraNav,
  currentLocale,
  hideNavLanguage,
}: {
  primaryNav: Array<PrimaryNavItemType> | undefined;
  extraNav: Array<ExtraNavItemType> | undefined;
  hideNavLanguage: boolean;
} & CurrentLocaleDataType) => {
  // #endregion
  const [isNavOpen, setIsNavOpen] = useState(false);
  const pathname = usePathname();
  const history = useRef(pathname);
  const localePathsMap = useContext(LanguageLinksContext);

  useEffect(() => {
    if (history.current !== pathname) {
      setIsNavOpen(false);

      history.current = pathname;
    }
  }, [pathname]);

  // Prevent body scroll when mobile menu is open and close mobile when it hits desktop breakpoint
  useLayoutEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isNavOpen]);

  const formattedNav = useMemo<AccordionItems>(
    () =>
      (primaryNav ?? []).map(item => ({
        id: item.id,
        label: item.childItems?.nodes?.length ? (
          <NavLabel>{item.label}</NavLabel>
        ) : (
          <NavLink
            onClick={() => {
              trackMenuClick(() => track, {
                category: item.label,
                label: item.label,
                isMobile: IS_MOBILE,
              });
            }}
            href={item.path ?? ''}
          >
            {item.label}
          </NavLink>
        ),
        href: item.path,
        isCollapsible: !!item.childItems?.nodes?.length,
        hasContentClickHandler: true,
        content: item.childItems?.nodes?.length ? (
          <ListLinkItems>
            {(item.childItems?.nodes ?? []).map(secItem => (
              <NavItemCard
                key={secItem?.id}
                as="li"
                heading={secItem?.label}
                description={secItem?.description}
                href={secItem?.path ?? ''}
                currentPath={pathname}
                mainNavOnClick={() => {
                  trackMenuClick(() => track, {
                    category: item.label,
                    label: secItem?.label,
                    isMobile: IS_MOBILE,
                  });
                  setIsNavOpen(false);
                }}
                subMenuList={{
                  items: formatCardMenuItem(secItem?.childItems?.nodes, {
                    tracker: () => {
                      setIsNavOpen(false);
                      track;
                    },
                    category: item.label,
                    isMobile: IS_MOBILE,
                  }),
                }}
              />
            ))}
          </ListLinkItems>
        ) : null,
      })),
    [primaryNav, pathname],
  );

  // #region JSX Render
  return formattedNav ? (
    <>
      <NavWrapper>
        {!hideNavLanguage && (
          <NavLanguage
            localePathsMap={localePathsMap}
            currentLocale={currentLocale}
            hide={isNavOpen}
          />
        )}
        <Hamburger
          toggled={isNavOpen}
          toggle={() => {
            setIsNavOpen(prev => !prev);
          }}
          size={24}
          color={WEB_PURPLE_REG}
          label="menu"
        />
      </NavWrapper>

      <ContentWrapperNav $isOpen={isNavOpen}>
        <Accordion hasBorder={false} items={formattedNav} />
        <StickyBottom $isSticky={isNavOpen}>
          {extraNav?.map(item => (
            <Fragment key={item.id}>
              {!item.css ? (
                <NextLink
                  href={item.path}
                  onClick={() =>
                    trackMenuClick(() => track, {
                      category: 'signup-login',
                      label: item.label ?? '',
                      isMobile: IS_MOBILE,
                    })
                  }
                  className="link-plain"
                >
                  {item.label}
                </NextLink>
              ) : (
                <Button
                  onClick={() =>
                    trackMenuClick(() => track, {
                      category: 'signup-login',
                      label: item.label ?? '',
                      isMobile: IS_MOBILE,
                    })
                  }
                  configuration={item.css.includes('ghost') ? 'ghost' : 'primary'}
                  href={item.path}
                >
                  {item.label}
                </Button>
              )}
            </Fragment>
          ))}
        </StickyBottom>
      </ContentWrapperNav>
    </>
  ) : null;
  // #endregion
};

export default HeaderNavMobile;
